// MemoryPanel — "What I remember about you"
// Drawer rendered above the chat body when the user toggles the memory icon.
// Lists memories the agent has stored and lets the user delete any of them.

import { useCallback, useEffect, useState } from 'react';
import {
  listMemories,
  deleteMemory,
  type MemoryEntry,
} from '../../api/agentMemory';
import { useAuth } from '../../contexts/AuthContext';
import styles from './AgentChat.module.css';

interface MemoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

export default function MemoryPanel({ isOpen, onClose, className }: MemoryPanelProps) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [memories, setMemories] = useState<MemoryEntry[]>([]);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) {
      setMemories([]);
      setError('Sign in to view your memories.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await listMemories(100, userId);
      setMemories(res.memories || []);
      setEnabled(!!res.enabled);
      setInitError(res.init_error ?? null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load memories';
      setError(msg);
      setMemories([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen) refresh();
  }, [isOpen, refresh]);

  if (!isOpen) return null;

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteMemory(id, userId);
      setMemories((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Delete failed';
      setError(msg);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={`${styles.memoryDrawer} ${className ?? ''}`}>
      <div className={styles.memoryHeader}>
        <strong className={styles.memoryTitle}>
          🧠 What I remember
          {memories.length > 0 && (
            <span className={styles.memoryCount}>({memories.length})</span>
          )}
        </strong>
        <div className={styles.memoryHeaderActions}>
          <button
            className={styles.iconBtn}
            onClick={refresh}
            title="Refresh"
            disabled={loading}
          >
            ↻
          </button>
          <button className={styles.iconBtn} onClick={onClose} title="Close">
            ✕
          </button>
        </div>
      </div>

      {enabled === false && !initError && (
        <p className={styles.hint}>
          Memory isn&apos;t available in this workspace yet. Ask your workspace admin to turn it on if you&apos;d like the agent to remember facts across sessions.
        </p>
      )}
      {enabled === false && initError && (
        <p className={styles.hint}>
          Memory is temporarily unavailable — the agent couldn&apos;t connect to its memory store. This has been logged for the team; try again later.
        </p>
      )}

      {loading && <p className={styles.hint}>Loading…</p>}
      {error && <p className={styles.error}>{error}</p>}

      {!loading && !error && memories.length === 0 && enabled !== false && (
        <p className={styles.hint}>
          No memories yet. The agent will remember facts you reveal across
          sessions.
        </p>
      )}

      <ul className={styles.memoryList}>
        {memories.map((m) => {
          const category =
            (m.metadata && (m.metadata as Record<string, unknown>).category) ||
            null;
          return (
            <li key={m.id} className={styles.memoryItem}>
              <div className={styles.memoryBody}>
                {category && (
                  <span className={`${styles.planServiceBadge} ${styles.svc_memory}`}>
                    {String(category)}
                  </span>
                )}
                <span className={styles.memoryText}>{m.memory}</span>
              </div>
              <button
                className={styles.delBtn}
                onClick={() => handleDelete(m.id)}
                disabled={deletingId === m.id}
                title="Forget this"
              >
                {deletingId === m.id ? '…' : '×'}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
